import { Outlet, useOutletContext, useSearchParams } from "react-router-dom";
import {
    Box,
    Button,
    Stack, Typography
} from "@mui/material";
import { LassoTextField } from "../../../components/LassoTextField";
import { colours } from "../../../StyleConstants";
import { Check, Close } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectSelectedDatabase } from "../../common/databaseSlice";
import { useGetSessionsQuery, useGetVisitByInstrumentQuery } from "../../../api/dashboardApiSlice";
import LassoSelect from "../../../components/LassoSelect";
import LoadingDashlet from "../../../components/loaders/LoadingDashlet";
import { MainDomainEnum } from "../dashboardTypes";

type Session = {
    label: string;
    description: string;
}

const DomainFilterLayout = () => {
    const context: any = useOutletContext();
    const database = useSelector(selectSelectedDatabase);
    const { data: sessions, isFetching: isFetchingSessions } = useGetSessionsQuery({ database });
    const { data: constructs, isFetching: isFetchingConstructs } = useGetVisitByInstrumentQuery({ database: database })

    const [instrumentFilter, setInstrumentFilter] = useState('');
    const [selectedSessionFilter, setSelectedSessionFilter] = useState<string[]>([]);
    const [selectedDomain, setSelectedDomain] = useState<string>(MainDomainEnum.ALL_DOMAINS);
    const [searchParams, setSearchParams] = useSearchParams();

    const allSessionLabels = useMemo(() => {
        return sessions ? sessions?.map(({ label }: Session) => label) : [];
    }, [sessions]);

    useEffect(() => {
        setSelectedDomain(searchParams.get("domain") ?? MainDomainEnum.ALL_DOMAINS);
        setSelectedSessionFilter(searchParams.get("session")?.split(",") ?? []);
        setInstrumentFilter(searchParams.get("instrument") ?? "");
    }, []);

    useEffect(() => {
        setSelectedSessionFilter(
            searchParams.get("session")?.split(",")
            ?? sessions?.map(({ label }: Session) => label) ?? []
        );
    }, [sessions]);

    const handleSessionChange = (event: any) => {
        const {
            target: { value },
        } = event;
        if (value.includes("select-all-sessions")) {
            setSearchParams((state) => {
                state.delete("session");
                return state;
            });
            if (sessions.length === selectedSessionFilter.length) {
                setSelectedSessionFilter([]);
            } else {
                setSelectedSessionFilter(allSessionLabels);
            }
            return;
        } else {
            const val = typeof value === 'string' ? value.split(',') : value;
            setSelectedSessionFilter(val);
            setSearchParams((state) => {
                state.set("session", val);
                return state;
            });
        }
    };

    const handleSessionToggleClick = () => {
        setSearchParams((state) => {
            state.delete("session");
            return state;
        });
        if (sessions.length === selectedSessionFilter.length) {
            setSelectedSessionFilter([]);
        } else {
            setSelectedSessionFilter(allSessionLabels);
        }
    }

    if (isFetchingSessions || isFetchingConstructs) {
        return <LoadingDashlet height={'calc(33% - 16px)'}/>
    }

    return (
        <Stack flexDirection={"column"} width="100%" height={"calc(100% - 62px)"} gap={2}>
            <Stack
                marginTop={4}
                gap={3}
                flexDirection={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
            >
                <LassoTextField
                    sx={{ flexGrow: 2 }}
                    value={instrumentFilter}
                    onChange={(e) => {
                        setInstrumentFilter(e.target.value);
                        setSearchParams((state) => {
                            state.set("instrument", e.target.value);
                            return state;
                        });
                    }}
                    size="small"
                    label={"Search Instrument"}
                />
                <LassoSelect
                    id={"domain-filter-session-select"}
                    sx={{
                        minWidth: '250px'
                    }}
                    label={'Filter by Session'}
                    options={sessions.map((group: any) => {
                        return {
                            value: group.label,
                            label: group.label
                        }
                    })}
                    multiple={true}
                    value={selectedSessionFilter}
                    onChange={handleSessionChange}
                />

                <Button
                    size="small"
                    variant={"outlined"}
                    color={sessions?.length === selectedSessionFilter?.length ? "error" : "primary"}
                    startIcon={sessions?.length === selectedSessionFilter?.length ? <Close/> : <Check/>}
                    disableRipple
                    onClick={handleSessionToggleClick}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{ minWidth: "206px" }}
                >
                    {
                        sessions?.length === selectedSessionFilter?.length
                            ? "Clear All Session(s)"
                            : "Select All Sessions"
                    }
                </Button>
            </Stack>
            <Stack flexDirection="row" gap={3} flexGrow={1} height={"100%"} overflow={"auto"}>
                <Stack gap={1} minWidth={"250px"} height={"100%"} overflow={"auto"} paddingRight={3}>
                    <Typography align={"center"} fontWeight={"bold"} marginRight={"24px"}>
                        Select Research Domains
                    </Typography>
                    {[MainDomainEnum.ALL_DOMAINS.toString(), MainDomainEnum.IMAGING.toString()].concat(Object.keys(constructs)).map((domain: string) =>
                        <Button
                            key={domain}
                            size="small"
                            variant={"outlined"}
                            disableRipple
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                setSearchParams((state) => {
                                    state.set("domain", domain);
                                    return state;
                                })
                                setSelectedDomain(domain)
                            }}
                            sx={
                                selectedDomain === domain
                                    ? {

                                        color: colours.orange.orange,
                                        borderColor: colours.orange.orange,
                                        fontWeight: 'bold',
                                        "&:hover": {
                                            color: colours.orange.orange,
                                            borderColor: colours.orange.orange,
                                            fontWeight: 'bold',
                                        }
                                    }
                                    : {}
                            }
                        >{domain}</Button>)}
                </Stack>
                <Box flexGrow={1}>
                    <Outlet
                        context={{
                            ...context,
                            selectedDomain: selectedDomain,
                            instrumentFilter: instrumentFilter,
                            selectedSessionFilter: selectedSessionFilter
                        }}
                    />
                </Box>
            </Stack>
        </Stack>
    )
}

export default DomainFilterLayout;